import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { zodResolver } from "@hookform/resolvers/zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

import { formatNumber } from "../../../../utils";
import { deliveryCostAmount } from "../../../../utils/delivery-cost";
import { Field } from "../../../DirectOfferDetails/OfferForm";
import { type PaymentMethod } from "../../../PaymentMethods/types";
import { completeDelivery } from "../../deliveryConfirmationApi";
import { offerDeliveryNoteUrl } from "../../utils";
import { responseError } from "../DeliveryCostNegotiationModal";
import { offerCurrencySymbol } from "../OfferCard";
import { DeliveryConfirmation, Offer } from "../OfferCard/interfaces";
import { OfferSummary } from "../OfferSummary";
import {
  type CompleteDeliveryValues,
  createCompleteDeliverySchema,
  DELIVERY_NOTE_TYPES,
  deliveryNoteIssue,
} from "./schema";

interface CompleteDeliveryModalProps {
  offer: Offer;
  paymentMethods: PaymentMethod[];
  onClose: () => void;
  onCompleted: () => void;
}

const includedDeliveryCost = (offer: Offer): number =>
  offer.includes_delivery_cost ? deliveryCostAmount(offer.demand_terms?.delivery_cost) : 0;

const initialValues = (offer: Offer): CompleteDeliveryValues => {
  const recorded = offer.delivery_recorded_by === "BUYER";
  const paid = recorded ? !!offer.final_payment_amount : true;
  const method = recorded ? offer.delivery_payment_method : offer.payment_method;
  return {
    delivered_quantity: String(
      Number(
        recorded && offer.delivered_quantity ? offer.delivered_quantity : offer.minimum_quantity
      )
    ),
    paid: paid ? "yes" : "no",
    payment_amount:
      recorded && offer.final_payment_amount
        ? String(Number(offer.final_payment_amount) - includedDeliveryCost(offer))
        : "",
    delivery_payment_method: method ? String(method) : "",
    includes_delivery_cost: !!offer.includes_delivery_cost,
  };
};

const demandCostOption = (offer: Offer) => {
  const terms = offer.demand_terms;
  const amount = deliveryCostAmount(terms?.delivery_cost);
  const paidElsewhere = !!terms?.delivery_cost_paid && terms.delivery_cost_paid_offer !== offer.id;
  return {
    available: !!terms && terms.delivery_cost_locked && amount > 0 && !paidElsewhere,
    amount,
  };
};

export const CompleteDeliveryModal = ({
  offer,
  paymentMethods,
  onClose,
  onCompleted,
}: CompleteDeliveryModalProps) => {
  const { t, i18n } = useTranslation();
  const currency = offerCurrencySymbol(t);
  const unit = offer.unit_of_measure?.code ?? "";
  const correcting = offer.delivery_recorded_by === "BUYER";
  const disputed = offer.delivery_confirmation === DeliveryConfirmation.DisputedByAggregator;
  const existingNote = correcting ? offerDeliveryNoteUrl(offer) : null;
  const demandCost = demandCostOption(offer);

  const [note, setNote] = useState<File | null>(null);
  const [noteError, setNoteError] = useState("");
  const [actionError, setActionError] = useState("");

  const resolver = useMemo(() => zodResolver(createCompleteDeliverySchema(t)), [t]);
  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CompleteDeliveryValues>({ resolver, defaultValues: initialValues(offer) });

  const paid = watch("paid") === "yes";
  const amount = watch("payment_amount");
  const includesCost = watch("includes_delivery_cost");
  const activeMethods = paymentMethods.filter(
    method => method.active || String(method.id) === watch("delivery_payment_method")
  );

  const onNoteChange = (file: File | null) => {
    setNote(file);
    setNoteError(deliveryNoteIssue(file, t) ?? "");
  };

  const onSubmit = handleSubmit(async values => {
    if (noteError) {
      return;
    }
    setActionError("");
    try {
      await completeDelivery(offer.id, {
        delivered_quantity: values.delivered_quantity,
        paid: values.paid === "yes",
        payment_amount: values.payment_amount,
        delivery_payment_method: values.delivery_payment_method,
        includes_delivery_cost: values.includes_delivery_cost,
        delivery_note: note,
      });
      onCompleted();
    } catch (error) {
      setActionError(responseError(error, t("completeDeliveryModal.error")));
    }
  });

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t(correcting ? "completeDeliveryModal.correctTitle" : "completeDeliveryModal.title")}
          </DialogTitle>
          <DialogDescription>
            {t("completeDeliveryModal.description", { aggregator: offer.tagged_aggregator?.name })}
          </DialogDescription>
        </DialogHeader>

        <OfferSummary offer={offer} />

        {disputed && (
          <Alert variant="warning">
            <AlertTitle>
              {t("completeDeliveryModal.disputedTitle", {
                aggregator: offer.tagged_aggregator?.name,
              })}
            </AlertTitle>
            <AlertDescription className="flex flex-col gap-1">
              <span>
                {(offer.delivery_dispute_topics ?? [])
                  .map(topic => t(`deliveryTopics.${topic}`))
                  .join(", ")}
              </span>
              {offer.delivery_dispute_comment && (
                <span className="whitespace-pre-wrap">{offer.delivery_dispute_comment}</span>
              )}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field
            id="delivered_quantity"
            label={`${t("completeDeliveryModal.quantity", { unit })} *`}
            errorMessage={errors.delivered_quantity?.message}
          >
            <Input
              id="delivered_quantity"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              aria-invalid={!!errors.delivered_quantity}
              {...register("delivered_quantity")}
            />
          </Field>

          <div className="flex flex-col gap-2">
            <Label className="font-semibold text-foreground">
              {`${t("completeDeliveryModal.paid")} *`}
            </Label>
            <Controller
              control={control}
              name="paid"
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="flex gap-6"
                  aria-label={t("completeDeliveryModal.paid")}
                >
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="yes" />
                    {t("common.yes")}
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="no" />
                    {t("common.no")}
                  </label>
                </RadioGroup>
              )}
            />
          </div>

          {paid && (
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="sm:w-56">
                <Field
                  id="payment_amount"
                  label={`${t("completeDeliveryModal.amount", { currency })} *`}
                  errorMessage={errors.payment_amount?.message}
                >
                  <Input
                    id="payment_amount"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    aria-invalid={!!errors.payment_amount}
                    {...register("payment_amount")}
                  />
                </Field>
              </div>
              <div className="flex-1">
                <Field
                  id="delivery_payment_method"
                  label={`${t("completeDeliveryModal.method")} *`}
                  errorMessage={errors.delivery_payment_method?.message}
                >
                  <Controller
                    control={control}
                    name="delivery_payment_method"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          id="delivery_payment_method"
                          className="w-full"
                          aria-invalid={!!errors.delivery_payment_method}
                        >
                          <SelectValue placeholder={t("completeDeliveryModal.methodPlaceholder")} />
                        </SelectTrigger>
                        <SelectContent position="popper" className="max-h-72">
                          {activeMethods.map(method => (
                            <SelectItem key={method.id} value={String(method.id)}>
                              {method.translations?.[i18n.language] ?? method.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>
              </div>
            </div>
          )}

          {paid && demandCost.available && (
            <Controller
              control={control}
              name="includes_delivery_cost"
              render={({ field }) => (
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={field.value}
                    disabled={!!offer.includes_delivery_cost}
                    onCheckedChange={checked => field.onChange(checked === true)}
                  />
                  <span className="flex flex-col gap-0.5">
                    {t("completeDeliveryModal.includesDeliveryCost", {
                      amount: `${currency}${formatNumber(demandCost.amount)}`,
                    })}
                    {includesCost && Number(amount) > 0 && (
                      <span className="text-muted-foreground">
                        {t("completeDeliveryModal.totalPaid", {
                          amount: `${currency}${formatNumber(Number(amount) + demandCost.amount)}`,
                        })}
                      </span>
                    )}
                  </span>
                </label>
              )}
            />
          )}

          <Field
            id="delivery_note"
            label={t("completeDeliveryModal.note")}
            helperText={t(
              existingNote ? "completeDeliveryModal.noteReplaces" : "completeDeliveryModal.noteHelp"
            )}
            errorMessage={noteError}
          >
            <Input
              id="delivery_note"
              type="file"
              accept={DELIVERY_NOTE_TYPES.join(",")}
              aria-invalid={!!noteError}
              onChange={event => onNoteChange(event.target.files?.[0] ?? null)}
            />
          </Field>

          {actionError && (
            <p role="alert" className="text-destructive text-sm">
              {actionError}
            </p>
          )}

          <DialogFooter>
            <WFPButton type="button" variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </WFPButton>
            <WFPButton type="submit" disabled={isSubmitting || !!noteError}>
              {isSubmitting && <Spinner className="size-4" />}
              {t(
                correcting
                  ? "completeDeliveryModal.submitCorrection"
                  : "completeDeliveryModal.submit"
              )}
            </WFPButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface PaymentMethodsState {
  listPaymentMethods?: { data?: { results?: PaymentMethod[] } };
}

export const ConnectedCompleteDeliveryModal = connect((state: PaymentMethodsState) => ({
  paymentMethods: state.listPaymentMethods?.data?.results ?? [],
}))(CompleteDeliveryModal);
